import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="passkeys"
export default class extends Controller {
  static targets = ["registerButton", "authenticateButton", "emailInput", "nicknameInput", "status"]

  async register(event) {
    event.preventDefault()
    if (!this._isSupported()) { return }
    this.setStatus("Starting passkey registration…")
    try {
      const resp = await fetch('/passkeys/registration/options', { method: 'POST', headers: { 'X-CSRF-Token': this.csrfToken(), 'Content-Type': 'application/json' } })
      if (!resp.ok) { throw new Error('feature disabled or server error') }
      const options = await resp.json()
      options.challenge = this._base64URLStringToBuffer(options.challenge)
      options.user.id = this._base64URLStringToBuffer(options.user.id)
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({ ...cred, id: this._base64URLStringToBuffer(cred.id) }))
      }

      const credential = await navigator.credentials.create({ publicKey: options })

      const payload = {
        credential: this._credentialToJSON(credential),
        nickname: this.nicknameInputTarget?.value
      }

      const verify = await fetch('/passkeys/registration/verify', { method: 'POST', headers: { 'X-CSRF-Token': this.csrfToken(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const data = await verify.json()
      if (!verify.ok || !data.success) throw new Error(data.error || 'Registration failed')
      this.setStatus('Passkey added!')
      window.location.reload()
    } catch (e) {
      this.setStatus(`Error: ${e.message}`)
      console.error(e)
    }
  }

  async authenticate(event) {
    event.preventDefault()
    if (!this._isSupported()) { return }
    this.setStatus("Starting passkey sign-in…")
    try {
      const email = this.hasEmailInputTarget ? (this.emailInputTarget.value || null) : null
      const resp = await fetch('/passkeys/authentication/options', { method: 'POST', headers: { 'X-CSRF-Token': this.csrfToken(), 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) })
      if (!resp.ok) { throw new Error('feature disabled or server error') }
      const options = await resp.json()
      options.challenge = this._base64URLStringToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(cred => ({ ...cred, id: this._base64URLStringToBuffer(cred.id) }))
      }

      const assertion = await navigator.credentials.get({ publicKey: options })
      const payload = { credential: this._assertionToJSON(assertion) }
      const verify = await fetch('/passkeys/authentication/verify', { method: 'POST', headers: { 'X-CSRF-Token': this.csrfToken(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const data = await verify.json()
      if (!verify.ok || !data.success) throw new Error(data.error || 'Authentication failed')
      window.location = data.redirect_to || '/'
    } catch (e) {
      this.setStatus(`Error: ${e.message}`)
      console.error(e)
    }
  }

  csrfToken() { return document.querySelector('meta[name="csrf-token"]').getAttribute('content') }

  setStatus(text) {
    if (this.hasStatusTarget) { this.statusTarget.textContent = text }
  }

  _credentialToJSON(cred) {
    return {
      id: cred.id,
      rawId: this._bufferToBase64URLString(cred.rawId),
      type: cred.type,
      response: {
        attestationObject: this._bufferToBase64URLString(cred.response.attestationObject),
        clientDataJSON: this._bufferToBase64URLString(cred.response.clientDataJSON)
      }
    }
  }

  _assertionToJSON(assertion) {
    return {
      id: assertion.id,
      rawId: this._bufferToBase64URLString(assertion.rawId),
      type: assertion.type,
      response: {
        authenticatorData: this._bufferToBase64URLString(assertion.response.authenticatorData),
        clientDataJSON: this._bufferToBase64URLString(assertion.response.clientDataJSON),
        signature: this._bufferToBase64URLString(assertion.response.signature),
        userHandle: assertion.response.userHandle ? this._bufferToBase64URLString(assertion.response.userHandle) : null
      }
    }
  }

  _bufferToBase64URLString(buffer) {
    const bytes = new Uint8Array(buffer)
    const binary = new TextDecoder("latin1").decode(bytes)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }

  _base64URLStringToBuffer(base64URLString) {
    const padding = '==='.slice((base64URLString.length + 3) % 4)
    const base64 = (base64URLString + padding).replace(/-/g, '+').replace(/_/g, '/')
    const str = atob(base64)
    const bytes = new Uint8Array(str.length)
    for (let i = 0; i < str.length; i++) { bytes[i] = str.charCodeAt(i) }
    return bytes.buffer
  }

  _isSupported() {
    if (!('PublicKeyCredential' in window)) {
      this.setStatus('Passkeys are not supported in this browser.')
      return false
    }
    if (!navigator.credentials || !navigator.credentials.create || !navigator.credentials.get) {
      this.setStatus('Credential Management API is not available (navigator.credentials missing).')
      return false
    }
    if (!window.isSecureContext) {
      this.setStatus('Passkeys require a secure context. Use https:// or http://localhost in development.')
      return false
    }
    return true
  }
}
